/**
 * Marketing site chrome — dark navy header + emerald accents + footer.
 * Wired to /api/v3/platform/announcements/live for the announcement banner.
 * BR-004 primary nav; BR-010 visual system.
 */
import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowRight, AlertCircle } from "lucide-react";
import { apiGet } from "@/lib/api";

const NAV = [
  { to: "/platform-overview", label: "Platform" },
  { to: "/solutions",         label: "Solutions" },
  { to: "/products",          label: "Products" },
  { to: "/pricing",           label: "Pricing" },
  { to: "/resources",         label: "Resources" },
  { to: "/company",           label: "Company" },
];

export function AnnouncementBar() {
  const [ann, setAnn] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    apiGet("/api/v3/platform/announcements/live")
      .then((rows) => rows?.[0] && setAnn(rows[0]))
      .catch(() => {});
  }, []);
  if (!ann || dismissed) return null;
  const tone = ann.kind === "maintenance"
    ? "bg-amber-500 text-slate-900"
    : ann.kind === "warning"
    ? "bg-red-500 text-white"
    : "bg-emerald-500 text-white";
  return (
    <div
      data-testid="marketing-announcement"
      className={`${tone} text-sm flex items-center justify-center gap-3 px-4 py-2`}
    >
      <AlertCircle className="w-4 h-4" />
      <span className="font-semibold">{ann.title}</span>
      <span className="opacity-90 hidden md:inline">— {ann.body_md}</span>
      <button
        data-testid="marketing-announcement-dismiss"
        onClick={() => setDismissed(true)}
        className="ml-auto opacity-70 hover:opacity-100"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export function MarketingHeader() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  return (
    <>
      <AnnouncementBar />
      <header
        data-testid="marketing-header"
        className="sticky top-0 z-30 bg-[#0B1F3A]/95 backdrop-blur-md border-b border-white/10"
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-6">
          <Link
            to="/"
            data-testid="marketing-logo"
            className="flex items-center"
            aria-label="iSpeedToClose home"
          >
            <span
              className="inline-flex items-center px-2.5 py-1.5 rounded-lg shadow-md ring-1 ring-amber-200/40"
              style={{
                backgroundColor: "#FDF8ED",
                backgroundImage: "linear-gradient(135deg, #FEFCF3 0%, #FDF3D8 100%)",
              }}
            >
              <img
                src="/brand/logo-full-color.png?v=1"
                alt="iSpeedToClose"
                className="h-7 md:h-8 w-auto"
                width="220"
                height="32"
              />
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1 ml-6">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className="px-3 py-2 text-sm text-slate-300 hover:text-white rounded-lg hover:bg-white/5"
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <Link
              to="/auth"
              data-testid="nav-login"
              className="text-sm text-slate-300 hover:text-white px-3 py-2"
            >
              Login
            </Link>
            <Link
              to="/auth?mode=signup"
              data-testid="nav-cta-primary"
              className="hidden md:inline-flex items-center gap-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold px-4 py-2"
            >
              Start Free Trial
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <button
              className="lg:hidden text-white"
              onClick={() => setOpen((o) => !o)}
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden bg-[#0B1F3A] border-t border-white/10 px-6 py-3 space-y-1">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="block py-2 text-slate-300 hover:text-white"
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/auth?mode=signup"
              className="block mt-2 rounded-lg bg-emerald-500 text-white text-center py-2 font-semibold"
            >
              Start Free Trial
            </Link>
          </div>
        )}
      </header>
    </>
  );
}

export function MarketingFooter() {
  return (
    <footer
      data-testid="marketing-footer"
      className="bg-[#0B1F3A] text-slate-400 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-5 gap-6 text-sm">
        <div className="col-span-2">
          <span
            className="inline-flex items-center px-2.5 py-1.5 rounded-lg shadow-md ring-1 ring-amber-200/40"
            style={{
              backgroundColor: "#FDF8ED",
              backgroundImage: "linear-gradient(135deg, #FEFCF3 0%, #FDF3D8 100%)",
            }}
          >
            <img
              src="/brand/logo-full-color.png?v=1"
              alt="iSpeedToClose"
              className="h-8 w-auto"
              width="220"
              height="32"
            />
          </span>
          <p className="mt-3 text-slate-400 max-w-xs">
            The AI Operating System for Real Estate Investors. Speed is the Deal.
          </p>
        </div>
        <div>
          <div className="text-white font-semibold mb-2">Platform</div>
          <ul className="space-y-1">
            <li><Link to="/products/property-workspace" className="hover:text-white">Property Workspace</Link></li>
            <li><Link to="/products/ai-acquisition-coach" className="hover:text-white">AI Coach</Link></li>
            <li><Link to="/products/executive-dashboard" className="hover:text-white">Executive Dashboard</Link></li>
            <li><Link to="/products/market-intelligence" className="hover:text-white">Market Intelligence</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white font-semibold mb-2">Solutions</div>
          <ul className="space-y-1">
            <li><Link to="/solutions/wholesalers" className="hover:text-white">Wholesalers</Link></li>
            <li><Link to="/solutions/fix-and-flip" className="hover:text-white">Fix &amp; Flip</Link></li>
            <li><Link to="/solutions/buy-and-hold" className="hover:text-white">Buy &amp; Hold</Link></li>
            <li><Link to="/solutions/brrrr" className="hover:text-white">BRRRR</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-white font-semibold mb-2">Company</div>
          <ul className="space-y-1">
            <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
            <li><Link to="/company" className="hover:text-white">About</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
            <li><Link to="/legal" className="hover:text-white">Legal</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 px-6 py-4 max-w-7xl mx-auto text-xs text-slate-500 flex flex-col md:flex-row items-center justify-between gap-2">
        <div>© {new Date().getFullYear()} iSpeedToClose · The AI Operating System for Real Estate Investors</div>
        <div className="flex gap-3">
          <Link to="/legal">Privacy</Link>
          <Link to="/legal">Terms</Link>
        </div>
      </div>
    </footer>
  );
}
